import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import ProjectTopNav from "../components/ProjectTopNav";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  LayoutDashboard, CalendarDays, Users, Clock, Target, TrendingUp,
  AlertTriangle, CheckCircle, ArrowLeft, Save, DollarSign, Briefcase,
  Settings, Activity, FileText, ListTodo, User, Calendar, Hash, Euro,
  Timer, Car, Receipt, Loader2, MapPin, Mail, Phone, Building2, FileEdit,
  Trash2, ShieldAlert, Search, Link2, ChevronDown, ImageIcon, Tag, Upload, X,
  Radar, Shield, Clipboard, FolderOpen, Calculator, GripVertical, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import AccommodationsAdmin from "./projects/AccommodationsAdmin";
import ProjectTeamTile from "./projects/ProjectTeamTile";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import {
  PROJECT_TYPE_LABELS, PROJECT_TYPE_COLORS, PROJECT_STATUS_LABELS,
  PROJECT_STATUS_COLORS, PROJECT_TYPE_OPTIONS, PROJECT_STATUS_OPTIONS,
  getProjectTypeColor,
} from "../config/projectTypeColors";

const API = "/api";
const AUTO_SAVE_DELAY = 600;

const BILLING_TYPE_CONFIG = {
  stunden_abrechenbar: { label: "Stunden abrechenbar" },
  pauschal: { label: "Pauschal" },
  sonstiges: { label: "Sonstiges" },
};

// ─── Save Status ───
function SaveIndicator({ status }) {
  if (status === "idle") return null;
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-all ${
      status === "saving" ? "bg-blue-50 text-blue-600 border border-blue-200"
        : status === "saved" ? "bg-green-50 text-green-600 border border-green-200"
        : "bg-red-50 text-red-600 border border-red-200"
    }`} data-testid="save-indicator">
      {status === "saving" && <><Loader2 className="w-3 h-3 animate-spin" />Speichern...</>}
      {status === "saved" && <><CheckCircle className="w-3 h-3" />Gespeichert</>}
      {status === "error" && "Fehler"}
    </div>
  );
}

// ─── Traffic Light ───
function TrafficLight({ percentage, size = "md", showLabel = false }) {
  const sizeClasses = { sm: "w-3 h-3", md: "w-4 h-4", lg: "w-6 h-6" };
  if (percentage === null || percentage === undefined) {
    return <div className="flex items-center gap-2"><div className={`${sizeClasses[size]} rounded-full bg-gray-300`} />{showLabel && <span className="text-sm text-gray-500">Keine Daten</span>}</div>;
  }
  let color, label, bgLight;
  if (percentage < 80) { color = "bg-green-500"; label = "Im Plan"; bgLight = "bg-green-100"; }
  else if (percentage <= 100) { color = "bg-yellow-500"; label = "Achtung"; bgLight = "bg-yellow-100"; }
  else { color = "bg-red-500"; label = "Ueberschritten"; bgLight = "bg-red-100"; }
  return (
    <div className={`flex items-center gap-2 ${showLabel ? `px-3 py-1.5 rounded-full ${bgLight}` : ""}`}>
      <div className={`${sizeClasses[size]} rounded-full ${color} shadow-sm`} />
      {showLabel && <span className="text-sm font-medium">{Math.round(percentage)}% - {label}</span>}
    </div>
  );
}

// ─── KPI Card ───
function KPIDisplay({ icon: Icon, label, value, unit, subtitle, status }) {
  // Status-Gradient passend zum Premium-Glass-Look
  const grads = {
    green:   "from-emerald-500/15 to-emerald-500/5 border-emerald-500/30",
    yellow:  "from-amber-500/15 to-amber-500/5 border-amber-500/30",
    red:     "from-red-500/15 to-red-500/5 border-red-500/30",
    neutral: "from-slate-500/12 to-slate-500/4 border-white/12",
  };
  const accentText = {
    green: "text-emerald-300",
    yellow: "text-amber-300",
    red: "text-red-300",
    neutral: "text-slate-300",
  };
  const g = grads[status] || grads.neutral;
  const at = accentText[status] || accentText.neutral;
  return (
    <div className={`card-glass-heavy bg-gradient-to-br ${g} border p-4`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${at}`} />{label}
      </div>
      <div className="text-2xl font-bold text-white tabular-nums">
        {value}{unit && <span className="text-sm font-normal ml-1 text-slate-300">{unit}</span>}
      </div>
      {subtitle && <div className="text-[11px] text-slate-400 mt-1">{subtitle}</div>}
    </div>
  );
}

// ─── Section ───
function Section({ title, icon: Icon, children, draggable = false, tone = "medium" }) {
  // tone: "heavy" (KPIs/Highlights/Team/Unterkünfte) | "medium" (Standard) | "solid" (Listen/Tabellen)
  const toneClass = tone === "heavy" ? "card-glass-heavy" : tone === "solid" ? "card-solid-dark" : "card-glass-medium";
  return (
    <div className={`${toneClass} flex flex-col overflow-hidden`}>
      <div
        className={`card-header ${draggable ? "tga-drag-handle cursor-grab active:cursor-grabbing select-none" : ""}`}
      >
        {draggable && <GripVertical className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
        {Icon && (
          <span className="card-header-icon">
            <Icon className="w-4 h-4" />
          </span>
        )}
        <span className="card-header-title">{title}</span>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

// ─── Inline Field ───
function InlineField({ label, icon: Icon, children, span2 }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </Label>
      {children}
    </div>
  );
}

// ─── Stable Text Input/Textarea ───
// BUGFIX: Tippen war nicht stabil weil onChange direkt gegen Parent-State + Server-Roundtrip
// kollidierte (Race Condition). Diese Wrapper halten den Wert lokal, committen erst bei
// onBlur (sicher) und/oder nach 1500 ms Idle. So bleiben Cursor-Position und alle Zeichen stabil
// auch wenn der Parent während des Tippens neu rendert oder eine Server-Response zurückkommt.
const StableTextarea = ({ value, onCommit, ...props }) => {
  const [local, setLocal] = useState(value || "");
  const focusedRef = useRef(false);
  const commitTimerRef = useRef(null);

  // Sync from outside ONLY when not focused (avoids clobbering user input mid-typing)
  useEffect(() => {
    if (!focusedRef.current) setLocal(value || "");
  }, [value]);

  const scheduleCommit = (next) => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => {
      if (next !== (value || "")) onCommit(next);
    }, 1500);
  };

  return (
    <Textarea
      {...props}
      value={local}
      onFocus={(e) => { focusedRef.current = true; props.onFocus?.(e); }}
      onChange={(e) => { setLocal(e.target.value); scheduleCommit(e.target.value); }}
      onBlur={(e) => {
        focusedRef.current = false;
        if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null; }
        if (local !== (value || "")) onCommit(local);
        props.onBlur?.(e);
      }}
    />
  );
};

const StableInput = ({ value, onCommit, ...props }) => {
  const [local, setLocal] = useState(value || "");
  const focusedRef = useRef(false);
  const commitTimerRef = useRef(null);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value || "");
  }, [value]);

  const scheduleCommit = (next) => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => {
      if (next !== (value || "")) onCommit(next);
    }, 1500);
  };

  return (
    <Input
      {...props}
      value={local}
      onFocus={(e) => { focusedRef.current = true; props.onFocus?.(e); }}
      onChange={(e) => { setLocal(e.target.value); scheduleCommit(e.target.value); }}
      onBlur={(e) => {
        focusedRef.current = false;
        if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null; }
        if (local !== (value || "")) onCommit(local);
        props.onBlur?.(e);
      }}
    />
  );
};


// ─── MAIN ───
export default function TGAoneProjectDashboard({ mode = "dashboard" } = {}) {
  // Browser-Titel synchron halten
  useEffect(() => {
    const suffix = mode === "daten" ? "Projektdaten" : "Projektübersicht";
    document.title = `${suffix} · TGAone`;
    return () => { document.title = "TGAone"; };
  }, [mode]);
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [crmContacts, setCrmContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickCreateFirma, setQuickCreateFirma] = useState("");
  const [quickCreateSaving, setQuickCreateSaving] = useState(false);
  const [ansprechpartner, setAnsprechpartner] = useState([]);
  const [showApDropdown, setShowApDropdown] = useState(false);
  const [controllingData, setControllingData] = useState(null);
  const [taskSummary, setTaskSummary] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [accommodationsList, setAccommodationsList] = useState([]);
  const [assignmentsToday, setAssignmentsToday] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first confirm, 2 = final confirm
  const [deleting, setDeleting] = useState(false);
  const [imageError, setImageError] = useState(false);

  const autoSaveRef = useRef(null);
  const pendingRef = useRef(null);
  const isAdmin = user?.permissions?.can_manage_projects || user?.role === "admin" || user?.role === "projektleiter";

  // ── Dashboard Layout (Drag & Drop Kacheln) ──
  // h-Werte = Mindesthöhe. Per User-Resize (südost-Eck) anpassbar.
  // v7 (27.02.2026): Tiles "settings" + "geo" entfernt — Inhalte sind jetzt unter /einstellungen.
  // Alte gespeicherte Layouts mit diesen IDs werden in mergeWithDefaults automatisch herausgefiltert.
  const DEFAULT_LAYOUT = useMemo(() => ([
    { i: "infos",        x: 0, y: 0,   w: 4, h: 12 },  // hat Projektbild → größer
    { i: "adresse",      x: 0, y: 12,  w: 4, h: 7  },
    { i: "kunde",        x: 0, y: 19,  w: 4, h: 8  },
    { i: "beschreibung", x: 0, y: 27,  w: 4, h: 5  },
    { i: "budget",       x: 4, y: 0,   w: 4, h: 6  },
    { i: "accommodations", x: 0, y: 32, w: 12, h: 4 },
    { i: "team",         x: 8, y: 0,   w: 4, h: 5  },
    { i: "tasks",        x: 8, y: 5,   w: 4, h: 5  },
    { i: "reports",      x: 8, y: 10,  w: 4, h: 4  },
  ]), []);
  // Stabiler Storage-Key OHNE Versions-Suffix, damit Code-Updates User-Layouts nicht zerstören.
  // Layout-Versionierung ggf. über eine _v-Property im Layout-Array (zukünftig).
  const LAYOUT_STORAGE_KEY = "tga-project-dashboard-layout";

  // Merge: stelle sicher, dass ALLE Default-Tiles vorhanden sind. Fehlende Tiles
  // werden UNTEN angefuegt damit sie nicht ueberlappen. Zusaetzlich werden zu schmale
  // Tiles (z.B. accommodations aus alter Layout-Version mit w<6) auf Default-Vollbreite
  // migriert, damit der Inhalt nicht abgeschnitten ist.
  const mergeWithDefaults = useCallback((saved) => {
    if (!Array.isArray(saved) || saved.length === 0) return DEFAULT_LAYOUT;
    const defaultsMap = new Map(DEFAULT_LAYOUT.map(d => [d.i, d]));

    // Bekannte Items durchlassen (User-Resize/Position bleibt erhalten),
    // unbekannte Tile-IDs filtern (Schutz vor "verwaisten" Items aus früheren Versionen).
    const migrated = saved.filter(s => defaultsMap.has(s.i));

    const presentKeys = new Set(migrated.map(s => s.i));
    const missing = DEFAULT_LAYOUT.filter(d => !presentKeys.has(d.i));
    if (missing.length === 0) return migrated;

    // Berechne maxY der gespeicherten Tiles (untere Kante)
    let nextY = 0;
    for (const item of migrated) {
      const bottom = (item.y || 0) + (item.h || 0);
      if (bottom > nextY) nextY = bottom;
    }
    // Platziere fehlende Tiles untereinander unterhalb der bestehenden mit voller Breite
    const placed = missing.map((m, idx) => ({
      ...m,
      x: 0,
      y: nextY + (idx * (m.h || 14)),
      w: 12,
    }));
    return [...migrated, ...placed];
  }, [DEFAULT_LAYOUT]);

  const [dashLayout, setDashLayout] = useState(() => {
    try {
      const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return mergeWithDefaults(parsed);
      }
    } catch (err) { console.warn("[Dashboard] LocalStorage Layout konnte nicht gelesen werden:", err); }
    return DEFAULT_LAYOUT;
  });
  const [layoutWidth, setLayoutWidth] = useState(() => (typeof window !== "undefined" ? Math.max(900, window.innerWidth - 280) : 1200));
  const layoutSaveTimerRef = useRef(null);
  const layoutLoadedRef = useRef(false);  // Verhindert Save-Storm vor erstem Backend-Load

  // Load remote layout on mount (Source of Truth); falls nicht vorhanden → Default.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/dashboard-layouts/project`);
        if (!res.ok) {
          console.warn("[Dashboard] Layout-Load nicht ok:", res.status);
          layoutLoadedRef.current = true;
          return;
        }
        const data = await res.json();
        if (Array.isArray(data.layout) && data.layout.length > 0) {
          const merged = mergeWithDefaults(data.layout);
          console.log("[Dashboard] Layout vom Backend geladen:", { items: merged.length });
          setDashLayout(merged);
          try { localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(merged)); } catch (err) { console.warn("[Dashboard] LocalStorage write failed:", err); }
        } else {
          // Kein Server-Layout vorhanden → Default initial speichern
          console.log("[Dashboard] Kein Backend-Layout — initialisiere mit Defaults");
          authFetch(`${API}/dashboard-layouts/project`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ layout: DEFAULT_LAYOUT }),
          }).catch((err) => console.warn("[Dashboard] Initial-Save failed:", err));
        }
      } catch (err) {
        console.warn("[Dashboard] Layout-Load Fehler:", err);
      } finally {
        // Markiere als geladen — ab jetzt darf onLayoutChange persistieren
        // (kleines Delay, damit der initiale onLayoutChange-Emit durch ist)
        setTimeout(() => { layoutLoadedRef.current = true; }, 250);
      }
    })();
  }, []);

  // Width-Observer für GridLayout
  useEffect(() => {
    const updateWidth = () => {
      setLayoutWidth(Math.max(900, window.innerWidth - 280));
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handleLayoutChange = useCallback((newLayout) => {
    // react-grid-layout gibt alle Items zurück; wir speichern i/x/y/w/h
    const compact = newLayout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
    setDashLayout(compact);
    // Schutz: Solange das Backend-Layout noch nicht geladen ist, NICHT speichern.
    // Sonst überschreibt der initiale onLayoutChange-Emit von react-grid-layout
    // ein gerade vom Backend geladenes User-Layout mit den Defaults.
    if (!layoutLoadedRef.current) {
      return;
    }
    try { localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(compact)); } catch (err) { console.warn("[Dashboard] LocalStorage write failed:", err); }
    // Debounced Backend-Save (1.2s nach letzter Änderung)
    if (layoutSaveTimerRef.current) clearTimeout(layoutSaveTimerRef.current);
    layoutSaveTimerRef.current = setTimeout(async () => {
      try {
        const res = await authFetch(`${API}/dashboard-layouts/project`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ layout: compact }),
        });
        if (!res.ok) {
          console.warn("[Dashboard] Layout-Save fehlgeschlagen:", res.status);
        } else {
          console.log("[Dashboard] Layout gespeichert:", { items: compact.length });
        }
      } catch (err) {
        console.warn("[Dashboard] Layout-Save Fehler:", err);
      }
    }, 1200);
  }, []);

  const handleLayoutReset = useCallback(async () => {
    setDashLayout(DEFAULT_LAYOUT);
    try { localStorage.removeItem(LAYOUT_STORAGE_KEY); } catch (err) { console.warn("[Dashboard] LocalStorage remove failed:", err); }
    try {
      await authFetch(`${API}/dashboard-layouts/project`, { method: "DELETE" });
      toast.success("Layout zurückgesetzt");
    } catch {
      toast.error("Konnte Layout nicht zurücksetzen");
    }
  }, [DEFAULT_LAYOUT]);

  // ── Load ──
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        // Heute (lokale Zeitzone) als YYYY-MM-DD für Assignment-Filter "Heute eingeplant"
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

        const [pRes, uRes, cRes, tRes, rRes, crm, aRes, accRes] = await Promise.all([
          authFetch(`${API}/projects`),
          authFetch(`${API}/users`),
          authFetch(`${API}/projects/${projectId}/controlling-data`).catch(() => ({ ok: false })),
          authFetch(`${API}/projects/${projectId}/tasks-summary`).catch(() => ({ ok: false })),
          authFetch(`${API}/projects/${projectId}/daily-reports`).catch(() => ({ ok: false })),
          authFetch(`${API}/crm/contacts`).catch(() => ({ ok: false })),
          authFetch(`${API}/assignments?from=${todayStr}&to=${todayStr}&project_id=${projectId}`).catch(() => ({ ok: false })),
          authFetch(`${API}/projects/${projectId}/accommodations`).catch(() => ({ ok: false })),
        ]);
        if (pRes.ok) { const ps = await pRes.json(); setProject(ps.find(p => p.id === projectId) || null); }
        if (uRes.ok) setUsers(await uRes.json());
        if (cRes.ok) setControllingData(await cRes.json());
        if (tRes.ok) setTaskSummary(await tRes.json());
        if (rRes.ok) { const rps = await rRes.json(); setRecentReports(rps.slice(0, 5)); }
        if (crm.ok) setCrmContacts(await crm.json());
        if (aRes.ok) setAssignmentsToday(await aRes.json());
        if (accRes.ok) setAccommodationsList(await accRes.json());
      } catch { toast.error("Fehler beim Laden"); }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  // ── Auto Save ──
  const patchProject = useCallback(async (fields) => {
    setSaveStatus("saving");
    try {
      const res = await authFetch(`${API}/projects/${projectId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
      if (res.ok) {
        const updated = await res.json();
        // BUGFIX: do NOT clobber fields the user is currently editing.
        // Race condition: user types "Hello" (debounce) -> PATCH -> user types "World" while in flight ->
        // server returns "Hello" -> setProject(updated) wipes "World". Result: characters vanish, cursor jumps.
        // Fix: keep server-side computed fields (id, updated_at, computed totals, …) but NEVER overwrite
        // any field that has unsaved local edits in pendingRef.
        setProject(prev => {
          if (!prev) return updated;
          const pending = pendingRef.current || {};
          const merged = { ...prev, ...updated };
          // Preserve any field still being edited locally (newer than the server response)
          Object.keys(pending).forEach((k) => { merged[k] = pending[k]; });
          return merged;
        });
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        setSaveStatus("error");
        toast.error("Fehler beim Speichern");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }, [projectId]);

  // Immediate save for dropdowns
  const saveField = useCallback((field, value) => {
    setProject(prev => ({ ...prev, [field]: value }));
    patchProject({ [field]: value });
  }, [patchProject]);

  // Debounced save for text/number fields
  const saveFieldDebounced = useCallback((field, value) => {
    setProject(prev => ({ ...prev, [field]: value }));
    pendingRef.current = { ...(pendingRef.current || {}), [field]: value };
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      if (pendingRef.current) { patchProject(pendingRef.current); pendingRef.current = null; }
    }, AUTO_SAVE_DELAY);
  }, [patchProject]);

  // Save address sub-field
  const saveAddress = useCallback((key, value) => {
    const addr = { ...(project?.address || {}), [key]: value };
    setProject(prev => ({ ...prev, address: addr }));
    pendingRef.current = { ...(pendingRef.current || {}), address: addr };
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      if (pendingRef.current) { patchProject(pendingRef.current); pendingRef.current = null; }
    }, AUTO_SAVE_DELAY);
  }, [project, patchProject]);

  // Save client address sub-field
  const saveClientAddress = useCallback((key, value) => {
    const addr = { ...(project?.client_address || {}), [key]: value };
    setProject(prev => ({ ...prev, client_address: addr }));
    pendingRef.current = { ...(pendingRef.current || {}), client_address: addr };
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      if (pendingRef.current) { patchProject(pendingRef.current); pendingRef.current = null; }
    }, AUTO_SAVE_DELAY);
  }, [project, patchProject]);

  // Upload image file (shared by file input, paste, and drag&drop)
  const uploadImageFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max. 10 MB erlaubt"); return; }
    setSaveStatus("saving");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authFetch(`${API}/projects/${projectId}/upload-image`, {
        method: "POST",
        body: formData,
        headers: {},
      });
      if (res.ok) {
        const updated = await res.json();
        setProject(updated);
        setImageError(false);
        setSaveStatus("saved");
        toast.success("Bild eingefügt");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Upload fehlgeschlagen");
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    } catch {
      toast.error("Upload fehlgeschlagen");
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }, [projectId]);

  // Handle paste events for images
  const handleImagePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) uploadImageFile(file);
        return;
      }
    }
  }, [uploadImageFile]);

  // Save geofencing sub-field
  const saveGeofencing = useCallback((key, value) => {
    const geo = { ...(project?.geofencing || { enabled: false, radius: 200, auto_clock_out: false, tolerance_minutes: 5, indoor_mode: false }), [key]: value };
    setProject(prev => ({ ...prev, geofencing: geo }));
    pendingRef.current = { ...(pendingRef.current || {}), geofencing: geo };
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      if (pendingRef.current) { patchProject(pendingRef.current); pendingRef.current = null; }
    }, AUTO_SAVE_DELAY);
  }, [project, patchProject]);

  // Fetch ansprechpartner for a CRM contact
  const fetchAnsprechpartner = useCallback(async (contactId) => {
    if (!contactId) { setAnsprechpartner([]); return []; }
    try {
      const res = await authFetch(`${API}/crm/contacts/${contactId}/ansprechpartner`);
      if (res.ok) { const data = await res.json(); setAnsprechpartner(data); return data; }
    } catch {}
    setAnsprechpartner([]);
    return [];
  }, []);

  // Select ansprechpartner from dropdown
  const selectAnsprechpartner = useCallback((ap) => {
    const updates = {
      contact_person: [ap.vorname, ap.nachname].filter(Boolean).join(" "),
      contact_phone: ap.telefon || "",
      contact_email: ap.email || "",
    };
    setProject(prev => ({ ...prev, ...updates }));
    patchProject(updates);
    setShowApDropdown(false);
  }, [patchProject]);

  // Link CRM contact to project
  const linkCrmContact = useCallback(async (contact) => {
    const updates = {
      crm_contact_id: contact.id,
      client_name: contact.firma || "",
      client_address: { street: contact.strasse || "", zip: contact.plz || "", city: contact.ort || "" },
      contact_person: [contact.vorname, contact.nachname].filter(Boolean).join(" ") || "",
      contact_phone: contact.telefon || contact.mobil || "",
      contact_email: contact.email || "",
    };
    // Fetch ansprechpartner and auto-select primary/first
    const apList = await fetchAnsprechpartner(contact.id);
    if (apList.length > 0) {
      const primary = apList.find(ap => ap.is_primary) || apList[0];
      updates.contact_person = [primary.vorname, primary.nachname].filter(Boolean).join(" ");
      updates.contact_phone = primary.telefon || "";
      updates.contact_email = primary.email || "";
    }
    setProject(prev => ({ ...prev, ...updates }));
    patchProject(updates);
    setShowContactDropdown(false);
    setContactSearch("");
  }, [patchProject, fetchAnsprechpartner]);

  // Unlink CRM contact
  const unlinkCrmContact = useCallback(() => {
    const updates = { crm_contact_id: null, client_name: "", client_address: null, contact_person: "", contact_phone: "", contact_email: "" };
    setProject(prev => ({ ...prev, ...updates }));
    patchProject(updates);
    setAnsprechpartner([]);
  }, [patchProject]);

  // Quick create CRM contact from project dashboard
  const quickCreateContact = useCallback(async () => {
    if (!quickCreateFirma.trim()) return;
    setQuickCreateSaving(true);
    const res = await authFetch(`${API}/crm/contacts`, { method: "POST", body: JSON.stringify({ firma: quickCreateFirma.trim() }) });
    if (res.ok) {
      const newContact = await res.json();
      setCrmContacts(prev => [newContact, ...prev]);
      linkCrmContact(newContact);
      setQuickCreateFirma("");
      setShowQuickCreate(false);
      toast.success("Kontakt erstellt und verknuepft");
    }
    setQuickCreateSaving(false);
  }, [quickCreateFirma, linkCrmContact]);

  useEffect(() => () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); }, []);

  // Fetch ansprechpartner when project has a linked CRM contact
  const lastFetchedContactId = useRef(null);
  useEffect(() => {
    const cid = project?.crm_contact_id;
    if (cid && cid !== lastFetchedContactId.current) {
      lastFetchedContactId.current = cid;
      fetchAnsprechpartner(cid);
    } else if (!cid) {
      lastFetchedContactId.current = null;
    }
  }, [project?.crm_contact_id, fetchAnsprechpartner]);

  // Close CRM dropdown on outside click
  useEffect(() => {
    if (!showContactDropdown && !showApDropdown) return;
    const handler = (e) => {
      if (showContactDropdown && !e.target.closest("[data-crm-dropdown]")) setShowContactDropdown(false);
      if (showApDropdown && !e.target.closest("[data-ap-dropdown]")) setShowApDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showContactDropdown, showApDropdown]);

  // ── Delete Project ──
  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      const res = await authFetch(`${API}/projects/${projectId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Projekt wurde geloescht");
        navigate("/app/projekte");
      } else {
        toast.error("Fehler beim Loeschen");
      }
    } catch { toast.error("Fehler beim Loeschen"); }
    finally { setDeleting(false); }
  }, [projectId, navigate]);

  const getUserName = (uid) => users.find(u => u.id === uid)?.name || "";

  const budgetPct = project?.budget && controllingData?.actual_costs ? (controllingData.actual_costs / project.budget) * 100 : null;
  const hoursPct = project?.planned_hours && controllingData?.total_work_hours ? (controllingData.total_work_hours / project.planned_hours) * 100 : null;

  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" /></div>;
  if (!project) return (
    <div className="flex flex-col items-center justify-center py-16">
      <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Projekt nicht gefunden</h2>
      <Button variant="outline" onClick={() => navigate("/app/projekte")}><ArrowLeft className="w-4 h-4 mr-2" />Zurück</Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="project-dashboard">
      {/* ═══ HEADER ═══ */}
      <div className="flex items-start justify-between" data-testid="project-header">
        <div className="flex items-start gap-3 pl-2">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-4 h-4 rounded-sm shrink-0" style={{ backgroundColor: project.color || "#6B7280" }} />
              <h1 className="text-xl font-bold text-gray-900" data-testid="project-name">{project.name}</h1>
              {project.project_number && <span className="text-sm text-gray-500 font-mono">{project.project_number}</span>}
            </div>
            <div className="text-[12px] font-medium text-blue-600 uppercase tracking-wider mt-0.5" data-testid="project-page-title">
              {mode === "daten" ? "Projektdaten" : "Projektübersicht"}
            </div>
            <TooltipProvider delayDuration={300}>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {/* ── Status Chip ── */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-0 rounded-md border border-gray-200 bg-gray-50 overflow-hidden" data-testid="chip-status">
                      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider px-2 py-1 bg-gray-100 border-r border-gray-200 select-none">Status</span>
                      {isAdmin ? (
                        <Select value={project.status || "aktiv"} onValueChange={(v) => saveField("status", v)}>
                          <SelectTrigger className="h-auto py-1 px-2 border-0 shadow-none bg-transparent text-[11px] font-medium min-w-[100px]" data-testid="quick-status">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: PROJECT_STATUS_COLORS[project.status] || "#6b7280" }} />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {PROJECT_STATUS_OPTIONS.map(o => (
                              <SelectItem key={o.value} value={o.value}>
                                <div className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PROJECT_STATUS_COLORS[o.value] }} />
                                  {o.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PROJECT_STATUS_COLORS[project.status] || "#6b7280" }} />
                          {PROJECT_STATUS_LABELS[project.status] || project.status}
                        </span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p className="text-xs">Aktueller Projektstand</p></TooltipContent>
                </Tooltip>

                {/* ── Typ Chip ── */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-0 rounded-md border border-gray-200 bg-gray-50 overflow-hidden" data-testid="chip-type">
                      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider px-2 py-1 bg-gray-100 border-r border-gray-200 select-none">Typ</span>
                      {isAdmin ? (
                        <Select value={project.project_type || "sonstiges"} onValueChange={(v) => saveField("project_type", v)}>
                          <SelectTrigger className="h-auto py-1 px-2 border-0 shadow-none bg-transparent text-[11px] font-medium min-w-[130px]" data-testid="quick-type">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: getProjectTypeColor(project.project_type) }} />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {PROJECT_TYPE_OPTIONS.map(o => (
                              <SelectItem key={o.value} value={o.value}>
                                <div className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: PROJECT_TYPE_COLORS[o.value] }} />
                                  {o.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium">
                          <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: getProjectTypeColor(project.project_type) }} />
                          {PROJECT_TYPE_LABELS[project.project_type] || project.project_type}
                        </span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p className="text-xs">Projektart bestimmt die Farbkodierung</p></TooltipContent>
                </Tooltip>

                {/* ── Projektleiter Chip ── */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-0 rounded-md border border-gray-200 bg-gray-50 overflow-hidden" data-testid="chip-manager">
                      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider px-2 py-1 bg-gray-100 border-r border-gray-200 select-none">Projektleiter</span>
                      {isAdmin ? (
                        <Select value={project.project_manager || "__none__"} onValueChange={(v) => saveField("project_manager", v === "__none__" ? "" : v)}>
                          <SelectTrigger className="h-auto py-1 px-2 border-0 shadow-none bg-transparent text-[11px] font-medium min-w-[120px]" data-testid="quick-manager">
                            <div className="flex items-center gap-1.5">
                              <User className="w-3 h-3 text-gray-400" />
                              <SelectValue placeholder="Nicht zugewiesen" />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__"><span className="text-gray-400">Nicht zugewiesen</span></SelectItem>
                            {users.filter(u => u.is_active !== false).map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium">
                          <User className="w-3 h-3 text-gray-400" />
                          {project.project_manager_name || "Nicht zugewiesen"}
                        </span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p className="text-xs">Verantwortliche Person fuer dieses Projekt</p></TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SaveIndicator status={saveStatus} />
          {isAdmin && (
            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { setDeleteStep(1); setShowDeleteConfirm(true); }} data-testid="delete-project-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* ═══ DELETE CONFIRMATION ═══ */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => { if (!open) { setShowDeleteConfirm(false); setDeleteStep(1); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="w-5 h-5" />
              {deleteStep === 1 ? "Projekt loeschen?" : "Endgueltig loeschen?"}
            </DialogTitle>
            <DialogDescription>
              {deleteStep === 1 ? (
                <span>Das Projekt <strong>"{project.name}"</strong> wird mit allen zugehoerigen Aufgaben und Einsatzplanungen unwiderruflich geloescht.</span>
              ) : (
                <span className="text-red-600 font-medium">
                  Letzte Warnung: Diese Aktion kann nicht rueckgaengig gemacht werden. Alle Daten zu diesem Projekt gehen verloren.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteStep(1); }} data-testid="delete-cancel-btn">
              Abbrechen
            </Button>
            {deleteStep === 1 ? (
              <Button variant="destructive" onClick={() => setDeleteStep(2)} data-testid="delete-confirm-step1-btn">
                Ja, loeschen
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="delete-confirm-final-btn">
                {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loeschen...</> : "Endgueltig loeschen"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ TOP NAVIGATION TABS (Sticky, geteilt mit allen Projekt-Unterseiten) ═══ */}
      <ProjectTopNav
        projectId={projectId}
        taskSummary={taskSummary}
        reportsCount={recentReports.length}
      />

      {/* ═══ KPIs (nur Projektübersicht) ═══ */}
      {mode === "dashboard" && (
      <div className="grid grid-cols-5 gap-4">
        <KPIDisplay icon={Clock} label="Ist-Stunden" value={controllingData?.total_work_hours?.toFixed(1) || "0"} unit="h" subtitle={project.planned_hours ? `von ${project.planned_hours}h geplant` : "Keine Planung"} status={hoursPct > 100 ? "red" : hoursPct > 80 ? "yellow" : "green"} />
        <KPIDisplay icon={Euro} label="Ist-Kosten" value={controllingData?.actual_costs?.toLocaleString("de-DE") || "0"} unit="EUR" subtitle={project.budget ? `von ${project.budget.toLocaleString("de-DE")} EUR Budget` : "Kein Budget"} status={budgetPct > 100 ? "red" : budgetPct > 80 ? "yellow" : "green"} />
        <KPIDisplay icon={Target} label="Budgetverbrauch" value={budgetPct !== null ? Math.round(budgetPct) : "--"} unit={budgetPct !== null ? "%" : ""} status={budgetPct > 100 ? "red" : budgetPct > 80 ? "yellow" : "green"} />
        <KPIDisplay icon={TrendingUp} label="Stundenverbrauch" value={hoursPct !== null ? Math.round(hoursPct) : "--"} unit={hoursPct !== null ? "%" : ""} status={hoursPct > 100 ? "red" : hoursPct > 80 ? "yellow" : "green"} />
        <div className="card-glass-heavy p-4 flex flex-col items-center justify-center">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-2">Kostenampel</div>
          <TrafficLight percentage={budgetPct} size="lg" showLabel />
        </div>
      </div>
      )}

      <div className="max-w-6xl mx-auto flex flex-col gap-5 mt-2" data-testid="project-detail-stack">
        {/* ═══ STAMMDATEN-TILES (nur Projektdaten) ═══ */}
        {mode === "daten" && (<>
        {/* Tile: Allgemeine Infos */}
        <div key="infos" data-testid="tile-infos" style={{ order: 1 }}>
        {/* ═══ LEFT: STAMMDATEN ═══ */}
          {/* Block 1: Allgemeine Infos */}
          <Section title="Allgemeine Infos" icon={Briefcase} tone="heavy">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <InlineField label="Projektname" icon={Hash} span2>
                <StableInput value={project.name || ""} onCommit={(v) => saveFieldDebounced("name", v)} className="h-8 text-[12px]" data-testid="field-name" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="Projektnummer" icon={Hash}>
                <Input value={project.project_number || ""} disabled className="h-8 text-[12px] bg-gray-50" data-testid="field-number" />
              </InlineField>
              <InlineField label="Kurzcode" icon={Hash}>
                <StableInput value={project.short_code || ""} onCommit={(v) => saveFieldDebounced("short_code", v)} placeholder="z.B. SN" className="h-8 text-[12px]" data-testid="field-shortcode" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="Projektname Anzeige" icon={Tag} span2>
                <StableInput value={project.display_name || ""} onCommit={(v) => saveFieldDebounced("display_name", v)} placeholder="z.B. Heizungsanlage Müller" className="h-8 text-[12px]" data-testid="field-display-name" disabled={!isAdmin} />
              </InlineField>

              {/* Haupt Projektbild — Upload + Paste + URL */}
              <div
                className="col-span-2"
                onPaste={isAdmin ? handleImagePaste : undefined}
                onDragOver={isAdmin ? (e) => { e.preventDefault(); e.stopPropagation(); } : undefined}
                onDrop={isAdmin ? (e) => { e.preventDefault(); e.stopPropagation(); const file = e.dataTransfer.files?.[0]; if (file) uploadImageFile(file); } : undefined}
                tabIndex={isAdmin ? 0 : undefined}
              >
                <Label className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1 mb-1">
                  <ImageIcon className="w-3 h-3" />Haupt Projektbild
                </Label>
                {project.project_image && !imageError ? (
                  <div className="relative group" data-testid="project-image-preview">
                    <img
                      src={project.project_image}
                      alt="Projektbild"
                      className="w-full h-[160px] object-cover rounded-lg border border-gray-200"
                      style={{ filter: "contrast(1.15) saturate(1.2) brightness(1.05)" }}
                      onError={() => setImageError(true)}
                      loading="lazy"
                      decoding="async"
                    />
                    {/* Premium-Overlay für Tiefe */}
                    <div
                      className="absolute inset-0 rounded-lg pointer-events-none"
                      style={{ background: "linear-gradient(to top, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.05) 50%, transparent 100%)" }}
                    />
                    {isAdmin && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="bg-white/90 border border-gray-200 rounded-full px-2 py-1 text-[9px] text-gray-500 font-medium flex items-center gap-1">
                          <Clipboard className="w-3 h-3" /> Strg+V zum Ersetzen
                        </span>
                        <button
                          onClick={() => { saveField("project_image", ""); setImageError(false); }}
                          className="bg-white/90 hover:bg-red-50 border border-gray-200 rounded-full p-1.5"
                          data-testid="project-image-remove"
                        >
                          <X className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : isAdmin ? (
                  <div className="space-y-2">
                    <label
                      className="flex flex-col items-center justify-center w-full h-[120px] border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
                      data-testid="project-image-upload-area"
                    >
                      <Upload className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500 font-medium">Bild hochladen, einfuegen oder hierher ziehen</span>
                      <span className="text-[10px] text-gray-400 mt-0.5">JPG, PNG, WebP — max. 10 MB — Strg+V / Drag &amp; Drop</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid="project-image-file-input"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) { setImageError(false); uploadImageFile(file); }
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-[10px] text-gray-400">oder URL eingeben</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <Input
                      value={project.project_image || ""}
                      onChange={(e) => { setImageError(false); saveFieldDebounced("project_image", e.target.value); }}
                      placeholder="https://beispiel.de/bild.jpg"
                      className="h-8 text-[12px]"
                      data-testid="field-project-image"
                    />
                  </div>
                ) : (
                  <div className="w-full h-[100px] bg-gray-100 rounded-lg border border-dashed border-gray-200 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>

              <InlineField label="Projektstart" icon={Calendar}>
                <Input type="date" value={project.start_date || ""} onChange={(e) => saveField("start_date", e.target.value)} className="h-8 text-[12px]" data-testid="field-start" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="Projektende" icon={Calendar}>
                <Input type="date" value={project.end_date || ""} onChange={(e) => saveField("end_date", e.target.value)} className="h-8 text-[12px]" data-testid="field-end" disabled={!isAdmin} />
              </InlineField>
            </div>
          </Section>
        </div>

        {/* Tile: Projektadresse */}
        <div key="adresse" data-testid="tile-adresse" style={{ order: 3 }}>
          {/* Block 2: Projektadresse (Baustelle / Einsatzort) */}
          <Section title="Projektadresse" icon={MapPin}>
            <p className="text-[10px] text-gray-400 mb-3">Baustelle / Einsatzort</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <InlineField label="Name" icon={Building2} span2>
                <Input value={project.address?.name || ""} onChange={(e) => saveAddress("name", e.target.value)} placeholder="z.B. Musashi GmbH" className="h-8 text-[12px]" data-testid="field-address-name" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="Strasse" icon={MapPin} span2>
                <Input value={project.address?.street || ""} onChange={(e) => saveAddress("street", e.target.value)} placeholder="Musterstr. 1" className="h-8 text-[12px]" data-testid="field-street" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="PLZ">
                <Input value={project.address?.zip || ""} onChange={(e) => saveAddress("zip", e.target.value)} placeholder="12345" className="h-8 text-[12px]" data-testid="field-zip" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="Ort">
                <Input value={project.address?.city || ""} onChange={(e) => saveAddress("city", e.target.value)} placeholder="Berlin" className="h-8 text-[12px]" data-testid="field-city" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="Zusatz" span2>
                <Input value={project.address?.zusatz || ""} onChange={(e) => saveAddress("zusatz", e.target.value)} placeholder="z.B. Halle 3, Bereich Nord" className="h-8 text-[12px]" data-testid="field-zusatz" disabled={!isAdmin} />
              </InlineField>
            </div>
          </Section>
        </div>

        {/* Tile: Kunde / Auftraggeber */}
        <div key="kunde" data-testid="tile-kunde" style={{ order: 2 }}>
          {/* Block 2b: Kundenadresse (aus CRM) */}
          <Section title="Kunde / Auftraggeber" icon={Building2}>
            <p className="text-[10px] text-gray-400 mb-3">Rechnungs- & Kommunikationsadresse</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {/* CRM Contact Selector */}
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1 mb-1">
                  <Link2 className="w-3 h-3" />CRM-Kontakt verknuepfen
                </Label>
                {project.crm_contact_id ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-8 px-3 flex items-center text-[12px] bg-blue-50 border border-blue-200 rounded-md text-blue-800">
                      <Building2 className="w-3 h-3 mr-2 text-blue-500" />
                      {project.client_name || "Verknuepfter Kontakt"}
                    </div>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-red-500" onClick={unlinkCrmContact} data-testid="unlink-crm-btn">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ) : isAdmin ? (
                  <div className="relative" data-crm-dropdown>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <Input
                        value={contactSearch}
                        onChange={(e) => { setContactSearch(e.target.value); setShowContactDropdown(true); }}
                        onFocus={() => setShowContactDropdown(true)}
                        placeholder="CRM-Kontakt suchen..."
                        className="h-8 text-[12px] pl-8"
                        data-testid="crm-contact-search"
                      />
                    </div>
                    {showContactDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {crmContacts
                          .filter(c => {
                            if (!contactSearch) return true;
                            const q = contactSearch.toLowerCase();
                            return (c.firma || "").toLowerCase().includes(q) || (c.nachname || "").toLowerCase().includes(q) || (c.vorname || "").toLowerCase().includes(q);
                          })
                          .slice(0, 10)
                          .map(c => (
                            <button
                              key={c.id}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0"
                              onClick={() => linkCrmContact(c)}
                              data-testid={`crm-contact-option-${c.id}`}
                            >
                              <div className="text-[12px] font-medium text-gray-800">{c.firma || "Ohne Firma"}</div>
                              <div className="text-[10px] text-gray-400">
                                {[c.vorname, c.nachname].filter(Boolean).join(" ")}
                                {c.ort && ` · ${c.ort}`}
                              </div>
                            </button>
                          ))}
                        {crmContacts.filter(c => {
                          if (!contactSearch) return true;
                          const q = contactSearch.toLowerCase();
                          return (c.firma || "").toLowerCase().includes(q) || (c.nachname || "").toLowerCase().includes(q);
                        }).length === 0 && (
                          <div className="px-3 py-2">
                            <p className="text-[11px] text-gray-400 mb-2">Keine Kontakte gefunden</p>
                            {!showQuickCreate ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowQuickCreate(true); setQuickCreateFirma(contactSearch); }}
                                className="w-full text-left text-[12px] text-blue-600 hover:text-blue-700 font-medium px-1 py-1"
                                data-testid="quick-create-contact-btn"
                              >
                                + Neuen Kontakt erstellen
                              </button>
                            ) : (
                              <div className="space-y-2" data-testid="quick-create-form">
                                <Input
                                  value={quickCreateFirma}
                                  onChange={(e) => setQuickCreateFirma(e.target.value)}
                                  placeholder="Firma *"
                                  className="h-8 text-[12px]"
                                  data-testid="quick-create-firma"
                                  autoFocus
                                  onKeyDown={(e) => { if (e.key === "Enter") quickCreateContact(); }}
                                />
                                <p className="text-[10px] text-gray-400">Weitere Details spaeter im CRM ergaenzen</p>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setShowQuickCreate(false)}>Abbrechen</Button>
                                  <Button size="sm" className="h-7 text-[11px]" onClick={quickCreateContact} disabled={quickCreateSaving || !quickCreateFirma.trim()} data-testid="quick-create-save-btn">
                                    {quickCreateSaving ? "..." : "Erstellen"}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-8 px-3 flex items-center text-[12px] text-gray-400 bg-gray-50 border rounded-md">Kein CRM-Kontakt verknuepft</div>
                )}
              </div>

              <InlineField label="Firma" icon={Building2} span2>
                <StableInput value={project.client_name || ""} onCommit={(v) => saveFieldDebounced("client_name", v)} placeholder="Firmenname" className="h-8 text-[12px]" data-testid="field-client" disabled={!isAdmin || !!project.crm_contact_id} />
              </InlineField>
              <InlineField label="Strasse" span2>
                <Input value={project.client_address?.street || ""} onChange={(e) => saveClientAddress("street", e.target.value)} placeholder="Kundenstrasse" className="h-8 text-[12px]" data-testid="field-client-street" disabled={!isAdmin || !!project.crm_contact_id} />
              </InlineField>
              <InlineField label="PLZ">
                <Input value={project.client_address?.zip || ""} onChange={(e) => saveClientAddress("zip", e.target.value)} placeholder="12345" className="h-8 text-[12px]" data-testid="field-client-zip" disabled={!isAdmin || !!project.crm_contact_id} />
              </InlineField>
              <InlineField label="Ort">
                <Input value={project.client_address?.city || ""} onChange={(e) => saveClientAddress("city", e.target.value)} placeholder="Berlin" className="h-8 text-[12px]" data-testid="field-client-city" disabled={!isAdmin || !!project.crm_contact_id} />
              </InlineField>

              <div className="col-span-2 border-t border-gray-100 my-1" />

              {/* Ansprechpartner: Dropdown wenn CRM verknüpft, sonst Freitext */}
              <InlineField label="Kunde Ansprechpartner" icon={User}>
                {project.crm_contact_id && isAdmin ? (
                  <div className="relative" data-ap-dropdown>
                    <button
                      onClick={() => setShowApDropdown(!showApDropdown)}
                      className="w-full h-8 px-3 text-left text-[12px] border rounded-md bg-white hover:bg-gray-50 flex items-center justify-between transition-colors"
                      data-testid="ap-dropdown-trigger"
                    >
                      <span className={project.contact_person ? "text-gray-800" : "text-gray-400"}>
                        {project.contact_person || "Ansprechpartner waehlen..."}
                      </span>
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    {showApDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {ansprechpartner.length > 0 ? (
                          ansprechpartner.map(ap => {
                            const name = [ap.vorname, ap.nachname].filter(Boolean).join(" ");
                            const isSelected = project.contact_person === name;
                            return (
                              <button
                                key={ap.id}
                                onClick={() => selectAnsprechpartner(ap)}
                                className={`w-full text-left px-3 py-2.5 border-b border-gray-50 last:border-0 transition-colors ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"}`}
                                data-testid={`ap-option-${ap.id}`}
                              >
                                <div className="text-[12px] font-medium text-gray-800">{name || "Ohne Name"}</div>
                                <div className="flex items-center gap-3 mt-0.5">
                                  {ap.position && <span className="text-[10px] text-gray-400">{ap.position}</span>}
                                  {ap.telefon && <span className="text-[10px] text-gray-400">{ap.telefon}</span>}
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-3 text-[11px] text-gray-400 text-center">
                            Keine Ansprechpartner hinterlegt.
                            <br />Bitte im CRM ergaenzen.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <StableInput value={project.contact_person || ""} onCommit={(v) => saveFieldDebounced("contact_person", v)} placeholder="Max Mustermann" className="h-8 text-[12px]" data-testid="field-contact-person" disabled={!isAdmin} />
                )}
              </InlineField>
              <InlineField label="Projektleiter Kunde" icon={User}>
                <StableInput value={project.project_client_manager || ""} onCommit={(v) => saveFieldDebounced("project_client_manager", v)} placeholder="Projektleiter des Kunden" className="h-8 text-[12px]" data-testid="field-project-client-manager" disabled={!isAdmin} />
              </InlineField>
              <InlineField label="Telefon" icon={Phone}>
                <StableInput value={project.contact_phone || ""} onCommit={(v) => saveFieldDebounced("contact_phone", v)} placeholder="+49 123 456789" className="h-8 text-[12px]" data-testid="field-contact-phone" disabled={!isAdmin || !!project.crm_contact_id} />
              </InlineField>
              <InlineField label="E-Mail" icon={Mail}>
                <StableInput type="email" value={project.contact_email || ""} onCommit={(v) => saveFieldDebounced("contact_email", v)} placeholder="name@firma.de" className="h-8 text-[12px]" data-testid="field-contact-email" disabled={!isAdmin || !!project.crm_contact_id} />
              </InlineField>
            </div>
          </Section>
        </div>

        {/* Tile: Projektbeschreibung */}
        <div key="beschreibung" data-testid="tile-beschreibung" style={{ order: 6 }}>
          <Section title="Projektbeschreibung" icon={FileEdit}>
            <div className="space-y-3">
              {/* Kurzbeschreibung (Listen / Karten / Mobile) */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block">
                  Projektkurzbeschreibung <span className="text-gray-500 normal-case font-normal">— wird in Listen unter dem Namen angezeigt (max. 120 Zeichen)</span>
                </label>
                <StableInput
                  value={project.short_description || ""}
                  onCommit={(v) => saveFieldDebounced("short_description", v)}
                  placeholder="z.B. Erneuerung Sicherheitsbeleuchtung"
                  maxLength={120}
                  className="h-8 text-[12px]"
                  data-testid="field-short-description"
                  disabled={!isAdmin}
                />
              </div>
              {/* Ausführliche Beschreibung (Detail / Info) */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block">
                  Ausführliche Projektbeschreibung
                </label>
                <StableTextarea
                  value={project.description || ""}
                  onCommit={(v) => saveFieldDebounced("description", v)}
                  placeholder="Detaillierte Infos, Umfang, Besonderheiten, Hinweise..."
                  rows={5}
                  className="text-[12px] resize-none"
                  data-testid="field-description"
                  disabled={!isAdmin}
                />
              </div>
            </div>
          </Section>
        </div>
        </>)}

        {/* ═══ MIDDLE: BUDGET & SETTINGS ═══ */}
        {/* Tile: Abrechnung & Budget */}
        {/* Tile: Abrechnung & Budget — ENTFERNT (jetzt unter /einstellungen) */}

        {/* Tile: Unterkünfte (auf beiden Seiten sichtbar — wichtig fürs operative Arbeiten) */}
        {isAdmin && (
          <div key="accommodations" data-testid="tile-accommodations" style={{ order: 5 }}>
            <Section title="Unterkünfte" icon={Settings} tone="heavy">
              <AccommodationsAdmin projectId={project.id} users={users} />
            </Section>
          </div>
        )}

        {/* ═══ RIGHT: TEAM, TASKS, REPORTS, NAV (nur Projektübersicht) ═══ */}
        {mode === "dashboard" && (<>
        {/* Tile: Team im Projekt */}
        <div key="team" data-testid="tile-team" style={{ order: 4 }}>
          <ProjectTeamTile
            project={project}
            users={users}
            assignmentsToday={assignmentsToday}
            accommodations={accommodationsList}
            isAdmin={isAdmin}
            onProjectUpdated={async () => {
              // Nach Backend-Patch: project + accommodations + assignments neu laden
              try {
                const r = await authFetch(`${API}/projects`);
                if (r.ok) { const ps = await r.json(); setProject(ps.find(p => p.id === projectId) || null); }
              } catch { /* noop */ }
            }}
          />
        </div>

        {/* Tile: Aufgaben-Übersicht */}
        <div key="tasks" data-testid="tile-tasks" style={{ order: 7 }}>
          <Section title="Aufgaben-Übersicht" icon={ListTodo}>
            {taskSummary ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="p-2.5 rounded bg-gray-50"><div className="text-xl font-bold">{taskSummary.total || 0}</div><div className="text-[10px] text-gray-500">Gesamt</div></div>
                  <div className="p-2.5 rounded bg-green-50"><div className="text-xl font-bold text-green-600">{taskSummary.by_status?.erledigt || 0}</div><div className="text-[10px] text-gray-500">Erledigt</div></div>
                  <div className="p-2.5 rounded bg-blue-50"><div className="text-xl font-bold text-blue-600">{taskSummary.by_status?.in_arbeit || 0}</div><div className="text-[10px] text-gray-500">In Arbeit</div></div>
                  <div className="p-2.5 rounded bg-red-50"><div className="text-xl font-bold text-red-600">{taskSummary.overdue || 0}</div><div className="text-[10px] text-gray-500">Überfällig</div></div>
                </div>
                {taskSummary.total > 0 && (
                  <div>
                    <div className="flex justify-between text-[10px] text-gray-600 mb-1"><span>Fortschritt</span><span>{Math.round(taskSummary.progress_percent || 0)}%</span></div>
                    <Progress value={taskSummary.progress_percent || 0} className="h-1.5" />
                  </div>
                )}
                <Button variant="outline" className="w-full text-[11px] h-8" onClick={() => navigate(`/app/projekte/${projectId}/aufgaben`)} data-testid="link-tasks">Alle Aufgaben anzeigen</Button>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500"><ListTodo className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">Keine Aufgaben</p></div>
            )}
          </Section>
        </div>

        {/* Tile: Letzte Bautagesberichte */}
        <div key="reports" data-testid="tile-reports" style={{ order: 8 }}>
          <Section title="Letzte Bautagesberichte" icon={FileText}>
            {recentReports.length > 0 ? (
              <div className="space-y-1.5">
                {recentReports.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/app/projekte/${projectId}/berichte`)}>
                    <div><div className="font-medium text-[12px]">{format(parseISO(r.date), "dd.MM.yyyy")}</div><div className="text-[10px] text-gray-500">{r.activities?.length || 0} Taetigkeiten</div></div>
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  </div>
                ))}
                <Button variant="outline" className="w-full mt-2 text-[11px] h-8" onClick={() => navigate(`/app/projekte/${projectId}/berichte`)} data-testid="link-reports">Alle Berichte</Button>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500"><FileText className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">Keine Berichte</p></div>
            )}
          </Section>
        </div>
        </>)}
      </div>
    </div>
  );
}
